import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  SimpleGrid,
  Text,
  Spinner,
  Badge,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import {
  BookOpen,
  MagnifyingGlass,
  ArrowSquareOut,
  Receipt,
  TrendUp,
  Percent,
} from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num as toNum, pct } from '../format';
import { IAuditoriaEmpresa, IContabil, IContabilLancamento } from '../types';
import { KpiCard } from '../components/KpiCard';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { EmptyState } from '../components/EmptyState';
import { UI } from '../ui';

interface ContabilScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  /** Cruzamentos: navegar a outro submódulo (Notas, Impostos, Auditorias, Dossiê...). */
  onScreen: (screen: string) => void;
}

const GLASS = {
  borderRadius: '16px',
  overflow: 'hidden',
  bg: 'rgba(255,255,255,0.7)',
  backdropFilter: 'blur(14px) saturate(160%)',
  border: '1px solid rgba(255,255,255,0.85)',
  boxShadow: '0 12px 34px -18px rgba(15,23,42,0.35)',
} as const;

type Aba = 'balancete' | 'dre' | 'indicadores' | 'lancamentos';

const TABS: { value: Aba; label: string }[] = [
  { value: 'balancete', label: 'Balancete' },
  { value: 'dre', label: 'DRE' },
  { value: 'indicadores', label: 'Indicadores' },
  { value: 'lancamentos', label: 'Lançamentos' },
];

const num = (v: number | null | undefined, suf = '') =>
  v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}${suf}`;
const money = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : formatCurrency(v);

function IndicadorCard({
  label,
  value,
  hint,
  color,
}: {
  label: string;
  value: string;
  hint?: string;
  color?: string;
}) {
  return (
    <Box bg="white" border="1px solid" borderColor={UI.border} borderRadius="14px" p="18px 20px">
      <Text
        fontSize="10.5px"
        fontWeight={700}
        letterSpacing=".9px"
        color={UI.muted}
        textTransform="uppercase"
      >
        {label}
      </Text>
      <Text fontSize="26px" fontWeight={700} mt={2} color={color ?? UI.ink} lineHeight="1">
        {value}
      </Text>
      {hint && (
        <Text fontSize="12px" color={UI.muted} mt={2}>
          {hint}
        </Text>
      )}
    </Box>
  );
}

export function ContabilScreen({ empresa, periodo, onScreen }: ContabilScreenProps) {
  const { getContabil, getContabilLancamentos } = useAuditoriaInteligente();
  const [aba, setAba] = useState<Aba>('balancete');
  const [buscaBal, setBuscaBal] = useState('');
  const [buscaLanc, setBuscaLanc] = useState('');

  const { data, isFetching } = useQuery<IContabil>({
    queryKey: ['ai-contabil', { id: empresa?.id, ...periodo }],
    queryFn: getContabil,
    enabled: !!empresa,
  });

  const { data: lancamentos, isFetching: loadingLanc } = useQuery<IContabilLancamento[]>({
    queryKey: ['ai-contabil-lancamentos', { id: empresa?.id, limit: 200, offset: 0, ...periodo }],
    queryFn: getContabilLancamentos,
    enabled: !!empresa && aba === 'lancamentos',
  });

  const balancete = useMemo(() => {
    const q = buscaBal.trim().toLowerCase();
    const todos = data?.balancete ?? [];
    if (!q) return todos;
    return todos.filter(c => `${c.classificacao} ${c.nome_conta}`.toLowerCase().includes(q));
  }, [data, buscaBal]);
  const totalAlertas = useMemo(() => (data?.balancete ?? []).filter(c => c.alerta).length, [data]);

  const lancFiltrados = useMemo(() => {
    const q = buscaLanc.trim().toLowerCase();
    const todos = lancamentos ?? [];
    if (!q) return todos;
    return todos.filter(l =>
      `${l.historico} ${l.documento} ${l.conta_debito_nome} ${l.conta_credito_nome}`
        .toLowerCase()
        .includes(q),
    );
  }, [lancamentos, buscaLanc]);

  // KPIs + sparkline da DRE (a partir de dre_mensal).
  const dreResumo = useMemo(() => {
    const m = data?.dre_mensal ?? [];
    const receita = m.reduce((s, d) => s + toNum(d.receita_bruta), 0);
    const resultado = m.reduce((s, d) => s + toNum(d.resultado), 0);
    const margemMedia = m.length
      ? m.reduce((s, d) => s + toNum(d.margem_liquida), 0) / m.length
      : 0;
    const spark = m.map(d => ({
      mes: d.mes,
      margem: toNum(d.margem_liquida),
      resultado: toNum(d.resultado),
    }));
    return { receita, resultado, margemMedia, spark };
  }, [data]);

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para ver o contábil."
        icon={BookOpen}
      />
    );
  }

  const ind = data?.indicadores;

  return (
    <Box>
      <Flex gap={1} borderBottom="1px solid #E7ECF2" mb={5} wrap="wrap">
        {TABS.map(t => {
          const isActive = t.value === aba;
          return (
            <Flex
              key={t.value}
              px={4}
              py={3}
              cursor="pointer"
              onClick={() => setAba(t.value)}
              borderBottom="2.5px solid"
              borderColor={isActive ? UI.brand : 'transparent'}
              color={isActive ? UI.brand : UI.muted}
              mb="-1px"
              _hover={{ color: isActive ? UI.brand : UI.ink }}
            >
              <Text fontSize="13px" fontWeight={700}>
                {t.label}
              </Text>
            </Flex>
          );
        })}
      </Flex>

      {isFetching && !data && aba !== 'lancamentos' && (
        <Box textAlign="center" py={16}>
          <Spinner />
        </Box>
      )}

      {aba === 'balancete' && (
        <Box {...GLASS}>
          <Flex px={5} pt={4} pb={3} justify="space-between" align="center" gap={3} wrap="wrap">
            <Flex align="center" gap={2}>
              <Text fontSize="13px" fontWeight={700} color={UI.ink}>
                Balancete
              </Text>
              {totalAlertas > 0 && (
                <Badge
                  style={{ background: UI.negSoft, color: UI.neg }}
                  borderRadius="full"
                  px={2.5}
                  fontSize="11px"
                  fontWeight={700}
                  textTransform="none"
                >
                  {totalAlertas} com alerta
                </Badge>
              )}
            </Flex>
            <InputGroup size="sm" w={{ base: '100%', md: '240px' }}>
              <InputLeftElement pointerEvents="none" color={UI.muted}>
                <MagnifyingGlass size={15} />
              </InputLeftElement>
              <Input
                placeholder="Classificação ou conta…"
                value={buscaBal}
                onChange={e => setBuscaBal(e.target.value)}
                borderRadius="10px"
                bg="white"
              />
            </InputGroup>
          </Flex>
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>Classificação</Th>
                  <Th>Conta</Th>
                  <Th isNumeric>Débito</Th>
                  <Th isNumeric>Crédito</Th>
                  <Th isNumeric>Saldo</Th>
                </Tr>
              </Thead>
              <Tbody>
                {balancete.map(c => (
                  <Tr
                    key={`${c.codigo_conta}-${c.classificacao}`}
                    _hover={{ bg: UI.rowHover }}
                    cursor={c.alerta ? 'pointer' : undefined}
                    onClick={c.alerta ? () => onScreen('auditorias') : undefined}
                    title={c.alerta ? 'Ver no Painel de Auditorias' : undefined}
                  >
                    <Td fontWeight={700}>{c.classificacao}</Td>
                    <Td maxW="280px" whiteSpace="normal">
                      {c.nome_conta}{' '}
                      {c.alerta && (
                        <Badge
                          style={{ backgroundColor: UI.warnSoft, color: UI.warn }}
                          borderRadius="full"
                          px={2}
                          fontSize="10.5px"
                          fontWeight={700}
                          textTransform="none"
                        >
                          {c.alerta}
                        </Badge>
                      )}
                    </Td>
                    <Td isNumeric>{formatCurrency(c.total_debito)}</Td>
                    <Td isNumeric>{formatCurrency(c.total_credito)}</Td>
                    <Td isNumeric fontWeight={700} color={c.alerta ? UI.neg : UI.ink}>
                      {formatCurrency(c.saldo)}
                    </Td>
                  </Tr>
                ))}
                {balancete.length === 0 && !isFetching && (
                  <Tr>
                    <Td colSpan={5} textAlign="center" py={8} color={UI.muted}>
                      {buscaBal ? 'Nenhuma conta com esse filtro.' : 'Sem balancete no período.'}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {aba === 'dre' && (
        <Box>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={5}>
            <KpiCard
              label="Receita bruta"
              value={formatCurrency(dreResumo.receita)}
              icon={Receipt}
              iconColor={UI.brand}
              iconBg={UI.infoSoft}
              onClick={() => onScreen('notas')}
              info={
                <InfoBloco
                  oque="Receita bruta total do período: faturamento na regra do Relatório de Faturamento do Domínio (acumuladores IFAT = S), agregado da DRE mensal."
                  dados="extract_acumuladores.saldo (ifat = 'S', mensal) — mês sem extração v2 usa as notas de venda + NFS-e"
                  filtro={FILTRO.empresa}
                />
              }
            />
            <KpiCard
              label="Resultado"
              value={formatCurrency(dreResumo.resultado)}
              icon={TrendUp}
              iconColor={dreResumo.resultado >= 0 ? UI.pos : UI.neg}
              iconBg={dreResumo.resultado >= 0 ? UI.posSoft : UI.negSoft}
              info={
                <InfoBloco
                  oque="Resultado líquido do período: receita bruta menos deduções (impostos), custos e despesas, somado dos meses da DRE."
                  dados="faturamento (acumuladores ifat = 'S') − extract_impostos.valor_total (PIS/COFINS/ICMS/ISS/IPI) − custos/despesas de extract_lancamentos (classificação 4%/5%/32%)"
                  filtro={FILTRO.empresa}
                />
              }
            />
            <KpiCard
              label="Margem líquida média"
              value={pct(dreResumo.margemMedia)}
              icon={Percent}
              iconColor="#8B5CF6"
              iconBg="#F0EAFB"
              info={
                <InfoBloco
                  oque="Média simples da margem líquida mensal (resultado ÷ receita bruta de cada mês) no período."
                  dados="margem_liquida da DRE mensal = resultado / receita_bruta (extract_acumuladores, extract_impostos, extract_lancamentos)"
                  filtro={FILTRO.empresa}
                />
              }
            />
          </SimpleGrid>

          {dreResumo.spark.length > 0 && (
            <Box
              bg={UI.glass}
              border="1px solid"
              borderColor={UI.glassBorder}
              borderRadius="16px"
              backdropFilter={UI.glassBlur}
              boxShadow={UI.glassShadow}
              p={5}
              mb={5}
            >
              <Text fontSize="13px" fontWeight={800} mb={3} color={UI.ink}>
                Margem líquida por mês
              </Text>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={dreResumo.spark}>
                  <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
                  <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
                  <YAxis fontSize={11} stroke={UI.muted} tickFormatter={v => `${v}%`} />
                  <Tooltip formatter={(v: number) => pct(v)} />
                  <Line
                    type="monotone"
                    dataKey="margem"
                    name="Margem líq."
                    stroke="#8B5CF6"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}

          <Grid templateColumns={{ base: '1fr', lg: '1fr 2fr' }} gap={5}>
            <Box {...GLASS}>
              <Flex px={5} pt={4} pb={2} justify="space-between" align="center">
                <Text fontSize="13px" fontWeight={700} color={UI.ink}>
                  DRE — resumo
                </Text>
                <Button
                  size="xs"
                  variant="ghost"
                  color={UI.brand}
                  rightIcon={<ArrowSquareOut size={12} weight="bold" />}
                  onClick={() => onScreen('impostos-apurados')}
                >
                  Impostos
                </Button>
              </Flex>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Tbody>
                    {(data?.dre_resumo ?? []).map(d => {
                      const total = d.tipo === '=';
                      return (
                        <Tr key={`${d.item}-${d.valor}`} bg={total ? UI.rowHover : undefined}>
                          <Td pl={d.nivel ? 8 : 4} fontWeight={total ? 800 : 600} color={UI.ink}>
                            {d.item}
                          </Td>
                          <Td
                            isNumeric
                            fontWeight={total ? 800 : 700}
                            color={d.valor < 0 ? UI.neg : UI.ink}
                          >
                            {formatCurrency(d.valor)}
                          </Td>
                        </Tr>
                      );
                    })}
                    {(data?.dre_resumo ?? []).length === 0 && (
                      <Tr>
                        <Td colSpan={2} textAlign="center" py={8} color={UI.muted}>
                          Sem DRE no período.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            <Box {...GLASS}>
              <Box px={5} pt={4} pb={2}>
                <Text fontSize="13px" fontWeight={700} color={UI.ink}>
                  Análise vertical (% sobre receita bruta)
                </Text>
              </Box>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={UI.rowHover}>
                    <Tr>
                      <Th>Mês</Th>
                      <Th isNumeric>Receita Bruta</Th>
                      <Th isNumeric>Deduções</Th>
                      <Th isNumeric>Custos</Th>
                      <Th isNumeric>Despesas</Th>
                      <Th isNumeric>Resultado</Th>
                      <Th isNumeric>Margem Líq.</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(data?.dre_mensal ?? []).map(d => (
                      <Tr key={d.mes} _hover={{ bg: UI.rowHover }}>
                        <Td fontWeight={700}>{d.mes}</Td>
                        <Td isNumeric>{formatCurrency(d.receita_bruta)}</Td>
                        <Td isNumeric>{pct(d.pct_deducoes)}</Td>
                        <Td isNumeric>{pct(d.pct_custos)}</Td>
                        <Td isNumeric>{pct(d.pct_despesas)}</Td>
                        <Td isNumeric fontWeight={700}>
                          {formatCurrency(d.resultado)}
                        </Td>
                        <Td
                          isNumeric
                          fontWeight={700}
                          color={d.margem_liquida >= 0 ? UI.pos : UI.neg}
                        >
                          {pct(d.margem_liquida)}
                        </Td>
                      </Tr>
                    ))}
                    {(data?.dre_mensal ?? []).length === 0 && (
                      <Tr>
                        <Td colSpan={7} textAlign="center" py={8} color={UI.muted}>
                          Sem dados mensais.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        </Box>
      )}

      {aba === 'indicadores' && ind && (
        <Box>
          <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={5}>
            <IndicadorCard
              label="Liquidez imediata"
              value={num(ind.liquidez_imediata)}
              hint="Disponível / Passivo Circ."
              color={(ind.liquidez_imediata ?? 0) >= 1 ? UI.pos : UI.warn}
            />
            <IndicadorCard
              label="Liquidez corrente"
              value={num(ind.liquidez_corrente)}
              hint="AC / PC"
              color={(ind.liquidez_corrente ?? 0) >= 1 ? UI.pos : UI.warn}
            />
            <IndicadorCard
              label="Liquidez seca"
              value={num(ind.liquidez_seca)}
              hint="(AC − Estoque) / PC"
            />
            <IndicadorCard
              label="Liquidez geral"
              value={num(ind.liquidez_geral)}
              hint="Ativo / Exigível"
            />
          </Grid>

          <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={5}>
            <IndicadorCard
              label="Prazo médio receb."
              value={ind.pmr === null ? '—' : `${ind.pmr} dias`}
              color={UI.brand}
            />
            <IndicadorCard
              label="Prazo médio pgto."
              value={ind.pmp === null ? '—' : `${ind.pmp} dias`}
              color={UI.pos}
            />
            <IndicadorCard
              label="Prazo médio estoc."
              value={ind.pme === null ? '—' : `${ind.pme} dias`}
              color={UI.warn}
            />
            <IndicadorCard
              label="Ciclo financeiro"
              value={ind.ciclo_financeiro === null ? '—' : `${ind.ciclo_financeiro} dias`}
              color="#8B5CF6"
            />
          </Grid>

          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5}>
            <Box
              bg={UI.glass}
              border="1px solid"
              borderColor={UI.glassBorder}
              borderRadius="16px"
              backdropFilter={UI.glassBlur}
              boxShadow={UI.glassShadow}
              p={5}
            >
              <Text fontSize="13px" fontWeight={700} mb={4} color={UI.ink}>
                Rentabilidade & caixa
              </Text>
              <Table variant="simple" size="sm">
                <Tbody>
                  <Tr>
                    <Td>EBITDA</Td>
                    <Td isNumeric fontWeight={700}>
                      {money(ind.ebitda)}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>Margem EBITDA</Td>
                    <Td isNumeric>{num(ind.margem_ebitda, '%')}</Td>
                  </Tr>
                  <Tr>
                    <Td>Margem operacional</Td>
                    <Td isNumeric>{num(ind.margem_operacional, '%')}</Td>
                  </Tr>
                  <Tr>
                    <Td>Ponto de equilíbrio</Td>
                    <Td isNumeric>{money(ind.ponto_equilibrio)}</Td>
                  </Tr>
                  <Tr>
                    <Td>Necessidade de capital de giro</Td>
                    <Td isNumeric color={(ind.ncg ?? 0) < 0 ? UI.neg : UI.ink}>
                      {money(ind.ncg)}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>Disponível</Td>
                    <Td isNumeric>{money(ind.disponivel)}</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>

            <Box
              bg={UI.glass}
              border="1px solid"
              borderColor={UI.glassBorder}
              borderRadius="16px"
              backdropFilter={UI.glassBlur}
              boxShadow={UI.glassShadow}
              p={5}
            >
              <Text fontSize="13px" fontWeight={700} mb={4} color={UI.ink}>
                Endividamento & estrutura patrimonial
              </Text>
              <Table variant="simple" size="sm">
                <Tbody>
                  <Tr>
                    <Td>Grau de endividamento</Td>
                    <Td isNumeric>
                      {ind.pct_endividamento === null ? '—' : `${ind.pct_endividamento}%`}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>Capital de terceiros</Td>
                    <Td isNumeric>
                      {ind.pct_capital_terceiros === null ? '—' : `${ind.pct_capital_terceiros}%`}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>Ativo circulante</Td>
                    <Td isNumeric fontWeight={700}>
                      {money(ind.ativo_circulante)}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>Passivo circulante</Td>
                    <Td isNumeric>{money(ind.passivo_circulante)}</Td>
                  </Tr>
                  <Tr>
                    <Td>Ativo total</Td>
                    <Td isNumeric>{money(ind.ativo_total)}</Td>
                  </Tr>
                  <Tr>
                    <Td>Passivo exigível</Td>
                    <Td isNumeric>{money(ind.passivo_exigivel)}</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
          </Grid>
        </Box>
      )}

      {aba === 'lancamentos' && (
        <Box {...GLASS}>
          <Flex px={5} pt={4} pb={3} justify="space-between" align="center" gap={3} wrap="wrap">
            <Text fontSize="13px" fontWeight={700} color={UI.ink}>
              Lançamentos{' '}
              <Text as="span" color={UI.faint} fontWeight={600}>
                ({lancFiltrados.length})
              </Text>
            </Text>
            <InputGroup size="sm" w={{ base: '100%', md: '300px' }}>
              <InputLeftElement pointerEvents="none" color={UI.muted}>
                <MagnifyingGlass size={15} />
              </InputLeftElement>
              <Input
                placeholder="Histórico, documento ou conta…"
                value={buscaLanc}
                onChange={e => setBuscaLanc(e.target.value)}
                borderRadius="10px"
                bg="white"
              />
            </InputGroup>
          </Flex>
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>Data</Th>
                  <Th>Documento</Th>
                  <Th>Débito</Th>
                  <Th>Crédito</Th>
                  <Th>Histórico</Th>
                  <Th isNumeric>Valor</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loadingLanc && (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8}>
                      <Spinner />
                    </Td>
                  </Tr>
                )}
                {!loadingLanc &&
                  lancFiltrados.map(l => (
                    <Tr
                      key={`${l.numero}-${l.documento}-${l.data}`}
                      cursor="pointer"
                      _hover={{ bg: UI.rowHover }}
                      onClick={() => onScreen('notas')}
                      title="Procurar a nota deste lançamento"
                    >
                      <Td>{l.data}</Td>
                      <Td fontWeight={700} color={UI.brand}>
                        {l.documento}
                      </Td>
                      <Td
                        maxW="160px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {l.conta_debito_nome}
                      </Td>
                      <Td
                        maxW="160px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {l.conta_credito_nome}
                      </Td>
                      <Td
                        maxW="240px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {l.historico}
                      </Td>
                      <Td isNumeric fontWeight={700}>
                        {formatCurrency(l.valor)}
                      </Td>
                    </Tr>
                  ))}
                {!loadingLanc && lancFiltrados.length === 0 && (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8} color={UI.muted}>
                      {buscaLanc
                        ? 'Nenhum lançamento com esse filtro.'
                        : 'Sem lançamentos no período.'}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
